import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Mail, Plus, Send, Users, RefreshCw, BarChart2, MousePointer, Sparkles, Loader2, Trash2, Play, Pause, ArrowDown, Zap, Clock, GitBranch, Eye, Code, Maximize2, X } from 'lucide-react';
import ModuleAutomationsPanel from '../components/automation/ModuleAutomationsPanel';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ABTestingTab = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTest, setNewTest] = useState({
    name: '', subject_a: '', subject_b: '', content_a: '', content_b: '', split_ratio: 50
  });

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/email/ab-tests`);
        setTests(res.data.ab_tests || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchTests();
  }, []);

  const handleCreate = async () => {
    if (!newTest.name || !newTest.subject_a || !newTest.subject_b) {
      toast.error('Fill in test name and both subject lines');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/email/ab-test`, newTest);
      toast.success('A/B test created!');
      setCreateOpen(false);
      setNewTest({ name: '', subject_a: '', subject_b: '', content_a: '', content_b: '', split_ratio: 50 });
      const res = await axios.get(`${API_URL}/api/email/ab-tests`);
      setTests(res.data.ab_tests || []);
    } catch (err) { toast.error('Failed to create test'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/email/ab-test/${id}`);
      toast.success('Test deleted');
      setTests(prev => prev.filter(t => t.id !== id));
    } catch (err) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-8 w-8 animate-spin text-emerald-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="ab-testing-tab">
      <div className="flex justify-end">
        <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setCreateOpen(true)} data-testid="create-ab-test-btn">
          <Plus className="h-4 w-4" />
          New A/B Test
        </Button>
      </div>

      {tests.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tests.map((test) => (
            <Card key={test.id} className="bg-white border-gray-border" data-testid={`ab-test-${test.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium text-text-primary">{test.name}</h3>
                  <Badge className={test.status === 'active' ? 'bg-emerald-brand text-white' : test.status === 'completed' ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white'}>
                    {test.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <p className="text-xs font-medium text-blue-600 mb-1">Variant A ({test.split_ratio}%)</p>
                    <p className="text-sm text-text-primary truncate">{test.variant_a?.subject}</p>
                    <div className="flex gap-2 mt-1 text-xs text-gray-medium">
                      <span>Sent: {test.variant_a?.sent || 0}</span>
                      <span>Opened: {test.variant_a?.opened || 0}</span>
                    </div>
                  </div>
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <p className="text-xs font-medium text-purple-600 mb-1">Variant B ({100 - test.split_ratio}%)</p>
                    <p className="text-sm text-text-primary truncate">{test.variant_b?.subject}</p>
                    <div className="flex gap-2 mt-1 text-xs text-gray-medium">
                      <span>Sent: {test.variant_b?.sent || 0}</span>
                      <span>Opened: {test.variant_b?.opened || 0}</span>
                    </div>
                  </div>
                </div>
                {test.winner && <Badge className="bg-emerald-brand text-white mb-2">Winner: Variant {test.winner}</Badge>}
                <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(test.id)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-8 text-center">
            <BarChart2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-medium mb-1">No A/B tests yet</p>
            <p className="text-sm text-gray-medium mb-4">Test different subject lines and content to optimize your emails</p>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create A/B Test
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New A/B Test</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Test Name</Label>
              <Input value={newTest.name} onChange={(e) => setNewTest(p => ({ ...p, name: e.target.value }))} className="mt-1" placeholder="e.g., Subject Line Test - March Newsletter" data-testid="ab-test-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-600">Variant A Subject</Label>
                <Input value={newTest.subject_a} onChange={(e) => setNewTest(p => ({ ...p, subject_a: e.target.value }))} className="mt-1" placeholder="Subject line A..." data-testid="ab-subject-a" />
              </div>
              <div>
                <Label className="text-purple-600">Variant B Subject</Label>
                <Input value={newTest.subject_b} onChange={(e) => setNewTest(p => ({ ...p, subject_b: e.target.value }))} className="mt-1" placeholder="Subject line B..." data-testid="ab-subject-b" />
              </div>
            </div>
            <div>
              <Label>Split Ratio (A : B)</Label>
              <Select value={String(newTest.split_ratio)} onValueChange={(v) => setNewTest(p => ({ ...p, split_ratio: Number(v) }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50% / 50%</SelectItem>
                  <SelectItem value="70">70% / 30%</SelectItem>
                  <SelectItem value="80">80% / 20%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreate} disabled={saving} data-testid="submit-ab-test">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const triggerOptions = [
  { value: 'new_subscriber', label: 'New Subscriber', icon: Users },
  { value: 'tag_added', label: 'Tag Added', icon: Zap },
  { value: 'form_submitted', label: 'Form Submitted', icon: Mail },
  { value: 'date_based', label: 'Date/Birthday', icon: Clock },
];

const actionOptions = [
  { value: 'send_email', label: 'Send Email' },
  { value: 'wait', label: 'Wait/Delay' },
  { value: 'condition', label: 'If/Else Condition' },
  { value: 'add_tag', label: 'Add Tag' },
  { value: 'remove_tag', label: 'Remove Tag' },
];

const AutomationWorkflowBuilder = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newAutomation, setNewAutomation] = useState({
    name: '',
    trigger: { type: 'new_subscriber', config: {} },
    steps: []
  });

  const fetchAutomations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/email/automations`);
      setAutomations(res.data.automations || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAutomations(); }, [fetchAutomations]);

  const addStep = (type) => {
    const step = {
      id: Date.now().toString(),
      type,
      config: type === 'wait' ? { duration: '1', unit: 'days' } :
              type === 'send_email' ? { subject: '', content: '' } :
              type === 'condition' ? { field: 'opened_last_email', operator: 'equals', value: 'true' } :
              {}
    };
    if (editingAutomation) {
      setEditingAutomation(prev => ({ ...prev, steps: [...prev.steps, step] }));
    } else {
      setNewAutomation(prev => ({ ...prev, steps: [...prev.steps, step] }));
    }
  };

  const removeStep = (stepId) => {
    if (editingAutomation) {
      setEditingAutomation(prev => ({ ...prev, steps: prev.steps.filter(s => s.id !== stepId) }));
    } else {
      setNewAutomation(prev => ({ ...prev, steps: prev.steps.filter(s => s.id !== stepId) }));
    }
  };

  const handleCreate = async () => {
    if (!newAutomation.name) { toast.error('Enter automation name'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/email/automations`, newAutomation);
      toast.success('Automation created!');
      setCreateOpen(false);
      setNewAutomation({ name: '', trigger: { type: 'new_subscriber', config: {} }, steps: [] });
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to create automation');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editingAutomation) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/email/automations/${editingAutomation.id}`, {
        name: editingAutomation.name,
        steps: editingAutomation.steps,
        trigger: editingAutomation.trigger,
        status: editingAutomation.status
      });
      toast.success('Automation saved!');
      setEditingAutomation(null);
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/email/automations/${id}`);
      toast.success('Automation deleted');
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleStatus = async (automation) => {
    const newStatus = automation.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API_URL}/api/email/automations/${automation.id}`, { status: newStatus });
      toast.success(`Automation ${newStatus}`);
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const currentSteps = editingAutomation ? editingAutomation.steps : newAutomation.steps;
  const currentTrigger = editingAutomation ? editingAutomation.trigger : newAutomation.trigger;

  // NOTE: The visual workflow JSX is stored as a JSX EXPRESSION (not a
  // component), which keeps React's reconciler treating its <Input>/<Select>
  // as the SAME nodes across renders. Defining `const WorkflowVisualizer = () => (...)`
  // inside this component would assign a new component identity each render and
  // remount every input on every keystroke — the textbook "letter-by-letter
  // typing" bug. JSX expression = stable subtree = no remount.
  const workflowJsx = (
    <div className="space-y-0" data-testid="workflow-visualizer">
      {/* Trigger Node */}
      <div className="flex items-center gap-3 p-3 bg-emerald-brand/10 border-2 border-emerald-brand rounded-xl">
        <Zap className="h-5 w-5 text-emerald-brand flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-emerald-brand font-medium uppercase tracking-wide">Trigger</p>
          <Select
            value={currentTrigger.type}
            onValueChange={(val) => {
              const trigger = { type: val, config: {} };
              editingAutomation ? setEditingAutomation(p => ({ ...p, trigger })) : setNewAutomation(p => ({ ...p, trigger }));
            }}
          >
            <SelectTrigger className="border-0 bg-transparent p-0 h-auto text-text-primary font-medium" data-testid="trigger-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {triggerOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Steps */}
      {currentSteps.map((step, i) => (
        <div key={step.id}>
          <div className="flex justify-center py-1"><ArrowDown className="h-4 w-4 text-gray-300" /></div>
          <div className={cn(
            "flex items-start gap-3 p-3 border-2 rounded-xl relative group",
            step.type === 'send_email' ? 'bg-blue-50 border-blue-200' :
            step.type === 'wait' ? 'bg-amber-50 border-amber-200' :
            step.type === 'condition' ? 'bg-purple-50 border-purple-200' :
            'bg-gray-50 border-gray-200'
          )}>
            {step.type === 'send_email' && <Mail className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />}
            {step.type === 'wait' && <Clock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />}
            {step.type === 'condition' && <GitBranch className="h-5 w-5 text-purple-500 flex-shrink-0 mt-1" />}
            {step.type === 'add_tag' && <Zap className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1" />}
            
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-medium">
                Step {i + 1}: {actionOptions.find(a => a.value === step.type)?.label}
              </p>
              {step.type === 'send_email' && (
                <Input
                  placeholder="Email subject line..."
                  value={step.config?.subject || ''}
                  onChange={(e) => {
                    const updated = [...currentSteps];
                    updated[i] = { ...step, config: { ...step.config, subject: e.target.value } };
                    editingAutomation ? setEditingAutomation(p => ({ ...p, steps: updated })) : setNewAutomation(p => ({ ...p, steps: updated }));
                  }}
                  className="mt-1 bg-white/70 border-0 text-sm"
                />
              )}
              {step.type === 'wait' && (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    value={step.config?.duration || '1'}
                    onChange={(e) => {
                      const updated = [...currentSteps];
                      updated[i] = { ...step, config: { ...step.config, duration: e.target.value } };
                      editingAutomation ? setEditingAutomation(p => ({ ...p, steps: updated })) : setNewAutomation(p => ({ ...p, steps: updated }));
                    }}
                    className="w-20 bg-white/70 border-0 text-sm"
                  />
                  <Select
                    value={step.config?.unit || 'days'}
                    onValueChange={(val) => {
                      const updated = [...currentSteps];
                      updated[i] = { ...step, config: { ...step.config, unit: val } };
                      editingAutomation ? setEditingAutomation(p => ({ ...p, steps: updated })) : setNewAutomation(p => ({ ...p, steps: updated }));
                    }}
                  >
                    <SelectTrigger className="w-24 bg-white/70 border-0 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {step.type === 'condition' && (
                <p className="text-sm text-gray-medium mt-1">If subscriber opened last email...</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-gray-medium hover:text-red-500"
              onClick={() => removeStep(step.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}

      {/* Add Step */}
      <div className="flex justify-center py-1"><ArrowDown className="h-4 w-4 text-gray-300" /></div>
      <div className="flex flex-wrap gap-2 justify-center">
        {actionOptions.map(action => (
          <Button
            key={action.value}
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => addStep(action.value)}
            data-testid={`add-step-${action.value}`}
          >
            <Plus className="h-3 w-3 mr-1" />
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-8 w-8 animate-spin text-emerald-brand" /></div>;

  if (editingAutomation) {
    return (
      <div className="space-y-4" data-testid="automation-editor">
        <div className="flex items-center justify-between">
          <div>
            <Input
              value={editingAutomation.name}
              onChange={(e) => setEditingAutomation(p => ({ ...p, name: e.target.value }))}
              className="text-lg font-heading font-semibold border-0 p-0 h-auto focus-visible:ring-0"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditingAutomation(null)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleSave} disabled={saving} data-testid="save-automation-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Automation
            </Button>
          </div>
        </div>
        <Card className="bg-white border-gray-border">
          <CardContent className="p-6">
            {workflowJsx}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={() => setCreateOpen(true)} data-testid="create-automation-btn">
          <Plus className="h-4 w-4" />
          New Automation
        </Button>
      </div>

      {automations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {automations.map((auto) => (
            <Card key={auto.id} className="bg-white border-gray-border" data-testid={`automation-card-${auto.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-text-primary">{auto.name}</h3>
                    <p className="text-xs text-gray-medium mt-0.5">
                      Trigger: {triggerOptions.find(t => t.value === auto.trigger?.type)?.label || auto.trigger?.type}
                    </p>
                  </div>
                  <Badge className={auto.status === 'active' ? 'bg-emerald-brand text-white' : 'bg-gray-500 text-white'}>
                    {auto.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-medium mb-4">
                  <span>{auto.steps?.length || 0} steps</span>
                  {auto.stats && (
                    <>
                      <span>Sent: {auto.stats.sent}</span>
                      <span>Opened: {auto.stats.opened}</span>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingAutomation(auto)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(auto)}
                  >
                    {auto.status === 'active' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(auto.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-8 text-center">
            <GitBranch className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-medium mb-1">No automations yet</p>
            <p className="text-sm text-gray-medium mb-4">Create your first email automation workflow</p>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Automation
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Email Automation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Automation Name</Label>
              <Input
                placeholder="e.g., Welcome Series"
                value={newAutomation.name}
                onChange={(e) => setNewAutomation(p => ({ ...p, name: e.target.value }))}
                className="mt-1"
                data-testid="automation-name-input"
              />
            </div>
            {workflowJsx}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreate} disabled={saving} data-testid="submit-automation-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Automation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const EmailMarketingPage = () => {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [fullPreview, setFullPreview] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    content_html: '',
    scheduled_at: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, campaignsRes, contactsRes] = await Promise.all([
        axios.get(`${API_URL}/api/email/stats`),
        axios.get(`${API_URL}/api/email/campaigns`),
        axios.get(`${API_URL}/api/email/contacts`),
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data.campaigns || []);
      setContacts(contactsRes.data.contacts || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.subject) {
      toast.error('Campaign name and subject line are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/email/campaigns`, newCampaign);
      toast.success(newCampaign.scheduled_at ? 'Campaign scheduled!' : 'Campaign sent!');
      setCreateDialogOpen(false);
      setNewCampaign({ name: '', subject: '', content_html: '', scheduled_at: '' });
      setPreviewMode(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create campaign');
      console.error('Create campaign error:', error);
    }
  };

  const handleGenerateEmail = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-email`, {
        subject: newCampaign.subject || 'Marketing email',
        goal: 'Engage customers',
        cta: 'Learn More',
        tone: 'professional'
      });
      setNewCampaign(prev => ({ ...prev, content_html: res.data.html_content }));
    } catch (error) {
      console.error('AI generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  const statusColors = {
    sent: 'bg-emerald-brand',
    scheduled: 'bg-blue-500',
    draft: 'bg-gray-500',
    sending: 'bg-amber-500',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="email-marketing-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Email Marketing<PageInfoTooltip pageKey="email-marketing" /></h1>
          <p className="text-gray-medium">Create and manage email campaigns</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" data-testid="create-campaign-btn">
              <Plus className="h-4 w-4" />
              Create Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Email Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Campaign Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Summer Sale Newsletter"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-2"
                    data-testid="campaign-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Subject Line</Label>
                  <Input
                    id="subject"
                    placeholder="e.g., Don't miss our biggest sale!"
                    value={newCampaign.subject}
                    onChange={(e) => setNewCampaign(prev => ({ ...prev, subject: e.target.value }))}
                    className="mt-2"
                    data-testid="campaign-subject-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Email Content</Label>
                  <div className="flex items-center gap-2">
                    <Select onValueChange={(tplKey) => {
                      const templates = {
                        welcome: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto"><div style="background:#10B981;padding:32px;border-radius:12px 12px 0 0;text-align:center"><h1 style="color:white;margin:0;font-size:28px">Welcome to {{company_name}}</h1><p style="color:#d1fae5;margin:8px 0 0">We're excited to have you on board</p></div><div style="background:#fff;padding:32px;border:1px solid #e5e7eb;border-radius:0 0 12px 12px"><p style="color:#374151;font-size:16px">Hi {{first_name}},</p><p style="color:#6b7280;line-height:1.6">Thank you for joining us. We're committed to helping your business grow with powerful automation tools.</p><div style="text-align:center;margin:24px 0"><a href="{{login_url}}" style="display:inline-block;background:#10B981;color:white;padding:14px 32px;text-decoration:none;border-radius:8px;font-weight:bold;font-size:16px">Get Started</a></div><p style="color:#9ca3af;font-size:12px">If you have any questions, reply to this email.</p></div></div>`,
                        newsletter: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto"><div style="background:linear-gradient(135deg,#1e293b,#334155);padding:32px;border-radius:12px 12px 0 0"><h1 style="color:white;margin:0;font-size:24px">{{company_name}} Newsletter</h1><p style="color:#94a3b8;margin:8px 0 0">{{month}} Edition</p></div><div style="background:#fff;padding:32px;border:1px solid #e5e7eb"><h2 style="color:#1e293b;font-size:20px;margin:0 0 16px">This Month's Highlights</h2><div style="background:#f8fafc;border-left:4px solid #10B981;padding:16px;margin:16px 0;border-radius:0 8px 8px 0"><h3 style="color:#10B981;margin:0 0 8px;font-size:16px">New Feature: Automation Engine</h3><p style="color:#64748b;margin:0;font-size:14px">Build powerful workflows with zero code. Automate lead assignment, follow-ups, and more.</p></div><div style="background:#f8fafc;border-left:4px solid #3b82f6;padding:16px;margin:16px 0;border-radius:0 8px 8px 0"><h3 style="color:#3b82f6;margin:0 0 8px;font-size:16px">Tip: GPS Attendance</h3><p style="color:#64748b;margin:0;font-size:14px">Use geofence zones to auto check-in employees when they arrive at the office.</p></div></div><div style="background:#f1f5f9;padding:24px;text-align:center;border-radius:0 0 12px 12px"><a href="{{cta_url}}" style="display:inline-block;background:#10B981;color:white;padding:12px 28px;text-decoration:none;border-radius:8px;font-weight:bold">Read More</a></div></div>`,
                        promotion: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto"><div style="background:linear-gradient(135deg,#f59e0b,#d97706);padding:40px;border-radius:12px 12px 0 0;text-align:center"><h1 style="color:white;margin:0;font-size:32px">Limited Time Offer</h1><p style="color:#fef3c7;font-size:18px;margin:8px 0 0">Save up to 40% this month</p></div><div style="background:#fff;padding:32px;border:1px solid #e5e7eb;text-align:center"><p style="color:#374151;font-size:16px;line-height:1.6">Upgrade your plan and unlock all premium features at a special discounted rate.</p><div style="background:#fefce8;border:2px dashed #f59e0b;border-radius:12px;padding:20px;margin:24px 0"><p style="color:#92400e;font-size:24px;font-weight:bold;margin:0">Use code: SAVE40</p><p style="color:#a16207;font-size:14px;margin:8px 0 0">Valid until end of month</p></div><a href="{{cta_url}}" style="display:inline-block;background:#f59e0b;color:white;padding:14px 32px;text-decoration:none;border-radius:8px;font-weight:bold;font-size:16px">Claim Your Discount</a></div><div style="padding:16px;text-align:center"><p style="color:#9ca3af;font-size:11px">Terms and conditions apply.</p></div></div>`,
                        followup: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto"><div style="padding:32px;background:#fff;border:1px solid #e5e7eb;border-radius:12px"><div style="border-bottom:1px solid #e5e7eb;padding-bottom:16px;margin-bottom:24px"><h1 style="color:#1e293b;margin:0;font-size:20px">Just checking in</h1></div><p style="color:#374151;font-size:15px;line-height:1.6">Hi {{first_name}},</p><p style="color:#6b7280;line-height:1.6">I wanted to follow up on our recent conversation. I'd love to help you get started with {{company_name}} and show you how it can transform your operations.</p><p style="color:#6b7280;line-height:1.6">Would you be available for a quick 15-minute call this week?</p><div style="margin:24px 0"><a href="{{meeting_url}}" style="display:inline-block;background:#3b82f6;color:white;padding:12px 24px;text-decoration:none;border-radius:8px;font-weight:bold">Schedule a Call</a></div><p style="color:#374151;font-size:14px">Best regards,<br><strong>{{sender_name}}</strong><br><span style="color:#9ca3af">{{sender_title}}</span></p></div></div>`,
                        invoice_reminder: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto"><div style="background:#ef4444;padding:24px;border-radius:12px 12px 0 0"><h1 style="color:white;margin:0;font-size:22px">Payment Reminder</h1></div><div style="background:#fff;padding:32px;border:1px solid #e5e7eb;border-radius:0 0 12px 12px"><p style="color:#374151;font-size:15px">Hi {{client_name}},</p><p style="color:#6b7280;line-height:1.6">This is a friendly reminder that invoice <strong>#{{invoice_number}}</strong> for <strong>{{amount}}</strong> is now overdue.</p><div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:16px;margin:20px 0"><p style="color:#991b1b;font-weight:bold;margin:0">Amount Due: {{amount}}</p><p style="color:#b91c1c;margin:4px 0 0;font-size:14px">Due Date: {{due_date}}</p></div><div style="text-align:center;margin:24px 0"><a href="{{payment_url}}" style="display:inline-block;background:#10B981;color:white;padding:12px 28px;text-decoration:none;border-radius:8px;font-weight:bold">Pay Now</a></div><p style="color:#9ca3af;font-size:12px">If you've already made the payment, please disregard this email.</p></div></div>`,
                      };
                      if (templates[tplKey]) {
                        setNewCampaign(prev => ({ ...prev, content_html: templates[tplKey] }));
                        toast.success(`Template "${tplKey}" loaded`);
                      }
                    }}>
                      <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Templates..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="welcome">Welcome Email</SelectItem>
                        <SelectItem value="newsletter">Newsletter</SelectItem>
                        <SelectItem value="promotion">Promotion</SelectItem>
                        <SelectItem value="followup">Follow-up</SelectItem>
                        <SelectItem value="invoice_reminder">Invoice Reminder</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleGenerateEmail}
                      disabled={generating}
                      data-testid="generate-email-btn"
                    >
                      {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                      AI Generate
                    </Button>
                    <div className="flex border rounded-md">
                      <button
                        className={cn("px-2.5 py-1.5 text-xs font-medium rounded-l-md transition-colors", !previewMode ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50")}
                        onClick={() => setPreviewMode(false)}
                        data-testid="editor-tab-btn"
                      >
                        <Code className="h-3.5 w-3.5 inline mr-1" />Editor
                      </button>
                      <button
                        className={cn("px-2.5 py-1.5 text-xs font-medium rounded-r-md transition-colors", previewMode ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50")}
                        onClick={() => setPreviewMode(true)}
                        data-testid="preview-tab-btn"
                      >
                        <Eye className="h-3.5 w-3.5 inline mr-1" />Preview
                      </button>
                    </div>
                  </div>
                </div>

                {/* Rich Text Toolbar */}
                {!previewMode && (
                  <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border border-b-0 border-gray-200 rounded-t-lg" data-testid="rich-text-toolbar">
                    {[
                      { cmd: 'bold', icon: 'B', cls: 'font-bold' },
                      { cmd: 'italic', icon: 'I', cls: 'italic' },
                      { cmd: 'underline', icon: 'U', cls: 'underline' },
                      { cmd: 'strikeThrough', icon: 'S', cls: 'line-through' },
                    ].map(b => (
                      <button key={b.cmd} className={cn("w-7 h-7 rounded text-xs hover:bg-gray-200 transition-colors", b.cls)}
                        onClick={() => { document.execCommand(b.cmd); document.getElementById('rich-editor')?.focus(); }}>{b.icon}</button>
                    ))}
                    <span className="w-px h-7 bg-gray-300 mx-1" />
                    <select className="h-7 text-xs border rounded px-1" onChange={e => { document.execCommand('fontSize', false, e.target.value); document.getElementById('rich-editor')?.focus(); }}>
                      <option value="3">Normal</option><option value="1">Small</option><option value="4">Large</option><option value="5">XL</option><option value="7">XXL</option>
                    </select>
                    <select className="h-7 text-xs border rounded px-1" onChange={e => { document.execCommand('fontName', false, e.target.value); document.getElementById('rich-editor')?.focus(); }}>
                      <option value="Arial">Arial</option><option value="Georgia">Georgia</option><option value="Times New Roman">Times New Roman</option><option value="Verdana">Verdana</option><option value="Courier New">Courier New</option><option value="Trebuchet MS">Trebuchet MS</option>
                    </select>
                    <span className="w-px h-7 bg-gray-300 mx-1" />
                    {['#000000','#ef4444','#10B981','#3b82f6','#f59e0b','#8b5cf6'].map(c => (
                      <button key={c} className="w-5 h-5 rounded-full border border-gray-200 mt-1" style={{ background: c }}
                        onClick={() => { document.execCommand('foreColor', false, c); document.getElementById('rich-editor')?.focus(); }} />
                    ))}
                    <span className="w-px h-7 bg-gray-300 mx-1" />
                    {[
                      { cmd: 'justifyLeft', icon: 'L' },
                      { cmd: 'justifyCenter', icon: 'C' },
                      { cmd: 'justifyRight', icon: 'R' },
                    ].map(b => (
                      <button key={b.cmd} className="w-7 h-7 rounded text-[10px] hover:bg-gray-200 transition-colors"
                        onClick={() => { document.execCommand(b.cmd); document.getElementById('rich-editor')?.focus(); }}>{b.icon}</button>
                    ))}
                    <button className="w-7 h-7 rounded text-[10px] hover:bg-gray-200" onClick={() => {
                      const url = prompt('Enter link URL:');
                      if (url) document.execCommand('createLink', false, url);
                      document.getElementById('rich-editor')?.focus();
                    }}>Link</button>
                  </div>
                )}

                {!previewMode ? (
                  <div
                    id="rich-editor"
                    contentEditable
                    className="min-h-[300px] p-4 border border-gray-200 rounded-b-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    style={{ fontFamily: 'Arial, sans-serif' }}
                    dangerouslySetInnerHTML={{ __html: newCampaign.content_html }}
                    onBlur={(e) => setNewCampaign(prev => ({ ...prev, content_html: e.target.innerHTML }))}
                    data-testid="campaign-rich-editor"
                  />
                ) : (
                  <div className="relative border rounded-lg bg-white" data-testid="campaign-preview-panel">
                    <div className="absolute top-2 right-2 z-10">
                      <button
                        onClick={() => setFullPreview(true)}
                        className="p-1.5 rounded-md bg-white shadow-sm border hover:bg-gray-50 transition-colors"
                        title="Full screen preview"
                        data-testid="fullscreen-preview-btn"
                      >
                        <Maximize2 className="h-3.5 w-3.5 text-gray-500" />
                      </button>
                    </div>
                    {newCampaign.content_html ? (
                      <div className="p-4">
                        {/* Email envelope header */}
                        <div className="border-b pb-3 mb-4 space-y-1">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="font-medium text-gray-700">Subject:</span>
                            <span>{newCampaign.subject || '(no subject)'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="font-medium text-gray-700">From:</span>
                            <span>your-business@company.com</span>
                          </div>
                        </div>
                        {/* Rendered HTML */}
                        <iframe
                          title="Email Preview"
                          srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6;color:#333;}img{max-width:100%;height:auto;}a{color:#10B981;}</style></head><body>${newCampaign.content_html}</body></html>`}
                          className="w-full min-h-[300px] border-0"
                          sandbox="allow-same-origin"
                          data-testid="email-preview-iframe"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400">
                        <Eye className="h-8 w-8 mb-2 text-gray-200" />
                        <p className="text-sm">No content yet</p>
                        <p className="text-xs mt-1">Switch to Editor to write content, or use AI to generate</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="schedule">Schedule (optional)</Label>
                <Input
                  id="schedule"
                  type="datetime-local"
                  value={newCampaign.scheduled_at}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className="mt-2"
                  data-testid="campaign-schedule-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setCreateDialogOpen(false)} data-testid="save-draft-btn">
                  Save Draft
                </Button>
                <Button className="flex-1 bg-emerald-brand hover:bg-emerald-hover" onClick={handleCreateCampaign} data-testid="send-campaign-btn">
                  <Send className="h-4 w-4 mr-2" />
                  {newCampaign.scheduled_at ? 'Schedule' : 'Send Now'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Full-screen email preview dialog */}
        <Dialog open={fullPreview} onOpenChange={setFullPreview}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fullscreen-preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Eye className="h-4 w-4" /> Email Preview
              </DialogTitle>
            </DialogHeader>
            {newCampaign.content_html ? (
              <div className="mt-4">
                <div className="border rounded-lg overflow-hidden">
                  {/* Email envelope */}
                  <div className="bg-gray-50 px-4 py-3 border-b space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-700 w-16">Subject:</span>
                      <span className="text-gray-600">{newCampaign.subject || '(no subject)'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-700 w-16">From:</span>
                      <span className="text-gray-600">your-business@company.com</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-700 w-16">To:</span>
                      <span className="text-gray-600">subscriber@example.com</span>
                    </div>
                  </div>
                  {/* Rendered email */}
                  <iframe
                    title="Full Email Preview"
                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6;color:#333;}img{max-width:100%;height:auto;}a{color:#10B981;}table{border-collapse:collapse;width:100%;}td,th{padding:8px;}</style></head><body>${newCampaign.content_html}</body></html>`}
                    className="w-full min-h-[500px] border-0"
                    sandbox="allow-same-origin"
                    data-testid="fullscreen-preview-iframe"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Eye className="h-12 w-12 mb-3 text-gray-200" />
                <p className="text-sm">No email content to preview</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-brand/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-brand" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-text-primary">{stats?.total_subscribers?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-gray-medium">Subscribers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-text-primary">{stats?.avg_open_rate || 0}%</p>
                <p className="text-[10px] sm:text-xs text-gray-medium">Avg Open Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <MousePointer className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-text-primary">{stats?.avg_click_rate || 0}%</p>
                <p className="text-[10px] sm:text-xs text-gray-medium">Avg Click Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Send className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-text-primary">{stats?.emails_sent_this_month?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-gray-medium">Sent This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-text-primary">{stats?.unsubscribes_this_month || 0}</p>
                <p className="text-[10px] sm:text-xs text-gray-medium">Unsubscribes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="campaigns" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="campaigns" className="text-xs sm:text-sm whitespace-nowrap">Campaigns</TabsTrigger>
          <TabsTrigger value="automations" className="text-xs sm:text-sm whitespace-nowrap">Automations</TabsTrigger>
          <TabsTrigger value="ab-testing" data-testid="tab-ab-testing" className="text-xs sm:text-sm whitespace-nowrap">A/B Testing</TabsTrigger>
          <TabsTrigger value="contacts" className="text-xs sm:text-sm whitespace-nowrap">Contacts</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-4">
          {campaigns.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map((campaign, i) => (
                <Card key={i} className="bg-white border-gray-border card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <Badge className={cn("text-white text-xs", statusColors[campaign.status])}>
                        {campaign.status}
                      </Badge>
                      <span className="text-xs text-gray-medium">
                        {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'Recent'}
                      </span>
                    </div>
                    <h3 className="font-medium text-text-primary mb-1">{campaign.name}</h3>
                    <p className="text-sm text-gray-medium mb-3">{campaign.subject}</p>
                    {campaign.stats && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-border">
                        <div className="text-center">
                          <p className="text-sm font-bold text-text-primary">{campaign.stats.sent || 0}</p>
                          <p className="text-xs text-gray-medium">Sent</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-text-primary">{campaign.stats.opens || 0}</p>
                          <p className="text-xs text-gray-medium">Opens</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-text-primary">{campaign.stats.clicks || 0}</p>
                          <p className="text-xs text-gray-medium">Clicks</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">No campaigns yet</p>
                <p className="text-sm text-gray-medium mb-4">Create your first email campaign</p>
                <Button className="bg-emerald-brand hover:bg-emerald-hover" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Campaign
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="automations" className="space-y-4">
          <AutomationWorkflowBuilder />
        </TabsContent>

        <TabsContent value="ab-testing" className="space-y-4">
          <ABTestingTab />
        </TabsContent>

        <TabsContent value="contacts">
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Email Contacts</CardTitle>
            </CardHeader>
            <CardContent>
              {contacts.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-border">
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-medium">Email</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-medium">Name</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-medium">Status</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-medium">Tags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.slice(0, 10).map((contact, i) => (
                        <tr key={i} className="border-b border-gray-border last:border-0">
                          <td className="py-3 px-2 text-text-primary">{contact.email}</td>
                          <td className="py-3 px-2 text-gray-medium">{contact.first_name} {contact.last_name}</td>
                          <td className="py-3 px-2">
                            <Badge variant={contact.status === 'subscribed' ? 'default' : 'secondary'} className={contact.status === 'subscribed' ? 'bg-emerald-brand' : ''}>
                              {contact.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            {contact.tags?.map((tag, j) => (
                              <Badge key={j} variant="outline" className="mr-1">{tag}</Badge>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-medium">No contacts yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <ModuleAutomationsPanel module="marketing" title="Marketing Automations" />
    </div>
  );
};

export default EmailMarketingPage;
